/**
 * Las frases y las decisiones del aviso de versión nueva (13-oct-2026). Sin React y sin importar nada: se prueba sola.
 *
 * El motor dice qué hay (`GET /actualizacion`); aquí se decide qué se enseña. Regla de siempre: ante la duda no se avisa
 * —un aviso que aparece cuando ya estás al día enseña a no hacer caso de los avisos—, y nada de lo que se dice promete
 * que se instala solo: el programa baja el instalador y lo abre, y quien instala es el autor.
 */
export type EstadoDeLaDescarga = { fase: "nada" | "bajando" | "comprobando" | "abierta" | "lista" | "fallo"; version?: string; recibido?: number; total?: number; error?: string };
export type Actualizacion = {
  actual: string;
  /** Solo viene si es posterior a la instalada. */
  ultima: { version: string; notas: string } | null;
  /** El programa puede bajarla él (hay instalador para este sistema, en su sitio y con su huella). */
  se_puede: boolean;
  motivo?: string;
  /** La página donde se descarga a mano. */
  enlace: string;
  estado: EstadoDeLaDescarga;
};

export const hayQueAvisar = (a: Actualizacion | null | undefined): a is Actualizacion => !!a?.ultima?.version;
export const enMarcha = (a: Actualizacion | null | undefined) => a?.estado?.fase === "bajando" || a?.estado?.fase === "comprobando";

export const titularDe = (a: Actualizacion) => `Hay una versión nueva: ${a.ultima?.version ?? ""}`;

/**
 * Qué se ofrece: que lo baje el programa, el enlace para hacerlo a mano, o nada (está en ello). Con el instalador ya
 * abierto se ofrece abrirlo OTRA VEZ: si el autor lo cerró sin instalar, no puede quedarse sin salida (equipo rojo, 13-oct).
 */
export type Boton = "actualizar" | "a_mano" | "reintentar" | "abrir";
export function botonDe(a: Actualizacion): Boton | null {
  if (enMarcha(a)) return null;
  if (!a.se_puede) return "a_mano";
  const f = a.estado.fase;
  return f === "fallo" ? "reintentar" : f === "abierta" || f === "lista" ? "abrir" : "actualizar";
}
/** ¿Se enseña también el enlace para descargarlo a mano? Siempre que se haya intentado y no esté en marcha. */
export const hayQueOfrecerloAMano = (a: Actualizacion) => ["abierta", "lista", "fallo"].includes(a.estado.fase);
/** Si el panel pierde al motor a media descarga (se reinició), lo que se dice mientras vuelve. */
export const FRASE_SIN_MOTOR = "He perdido el contacto con HumanInk en este ordenador. Si se ha reiniciado, en un momento podrás volver a pulsar.";

const megas = (n: number) => `${Math.round(n / 1048576)} MB`;
/** Cómo va, en una línea. `null` si todavía no se ha pedido nada. */
export function fraseDelEstado(a: Actualizacion): string | null {
  const e = a.estado;
  if (e.fase === "bajando") {
    const r = e.recibido ?? 0, t = e.total ?? 0;
    return t > 0 ? `Bajando el instalador: ${Math.min(100, Math.floor((100 * r) / t))} % de ${megas(t)}.` : "Bajando el instalador…";
  }
  if (e.fase === "comprobando") return "Comprobando que es el instalador que se publicó…";
  if (e.fase === "abierta") return "Se ha abierto el instalador. Sigue sus pasos y, cuando termine, cierra Word y vuelve a abrirlo. Si lo cerraste sin instalar, puedes abrirlo otra vez.";
  if (e.fase === "lista") return "El instalador está bajado y comprobado, pero no he podido abrirlo.";
  if (e.fase === "fallo") return e.error || "No se ha podido bajar el instalador.";
  return null;
}

/** Lo que se dice cuando el programa no puede bajarla él: que se hace a mano, y dónde. */
export const FRASE_A_MANO = "Esta versión se instala a mano: descarga el instalador y ábrelo.";
export const rotuloDe = (b: Boton) => ({ actualizar: "Actualizar", a_mano: "Ir a la descarga", reintentar: "Volver a intentarlo", abrir: "Abrir el instalador otra vez" })[b];
